import React, { useState } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Check, X, Brain, Pill, FileText, Syringe } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../ui/textarea';

// ============================================================
// PHQ-9 Form
// ============================================================
const PHQ9_QUESTIONS = [
  "Little interest or pleasure in doing things",
  "Feeling down, depressed, or hopeless",
  "Trouble falling or staying asleep, or sleeping too much",
  "Feeling tired or having little energy",
  "Poor appetite or overeating",
  "Feeling bad about yourself",
  "Trouble concentrating on things",
  "Moving or speaking slowly, or being fidgety",
  "Thoughts of being better off dead or hurting yourself",
];
const SCORE_LABELS = ["Not at all (0)", "Several days (1)", "More than half (2)", "Nearly every day (3)"];

function PHQ9Form({ onSubmit, submitting }) {
  const [scores, setScores] = useState(Array(9).fill(0));
  const total = scores.reduce((a, b) => a + b, 0);
  const severity = total < 5 ? 'None' : total < 10 ? 'Mild' : total < 15 ? 'Moderate' : total < 20 ? 'Mod. Severe' : 'Severe';
  const sevColor = total < 5 ? 'text-emerald-600' : total < 10 ? 'text-amber-600' : total < 15 ? 'text-orange-600' : 'text-red-600';

  return (
    <div className="space-y-3" data-testid="phq9-form">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Over the last 2 weeks, how often have you been bothered by:</span>
        <div className="text-right">
          <span className="text-lg font-bold font-mono">{total}</span><span className="text-xs text-muted-foreground">/27</span>
          <Badge className={`ml-2 text-[9px] ${sevColor}`}>{severity}</Badge>
        </div>
      </div>
      {PHQ9_QUESTIONS.map((q, i) => (
        <div key={i} className="flex items-start gap-2 p-2 rounded border bg-white" data-testid={`phq9-q-${i}`}>
          <span className="text-[10px] text-muted-foreground shrink-0 w-4 pt-1">{i + 1}.</span>
          <span className="text-xs flex-1 leading-snug">{q}</span>
          <div className="flex gap-1 shrink-0">
            {[0, 1, 2, 3].map(v => (
              <button key={v} className={`w-7 h-7 rounded text-xs font-mono font-bold transition-all ${scores[i] === v ? 'bg-primary text-white shadow-sm' : 'bg-muted/50 text-muted-foreground hover:bg-muted'}`}
                onClick={() => { const s = [...scores]; s[i] = v; setScores(s); }}
                data-testid={`phq9-score-${i}-${v}`}
              >{v}</button>
            ))}
          </div>
        </div>
      ))}
      <div className="text-[9px] text-muted-foreground">0=Not at all, 1=Several days, 2=More than half, 3=Nearly every day</div>
      <Button className="w-full" disabled={submitting} onClick={() => onSubmit({ task_type: 'phq9', payload: { scores } })} data-testid="phq9-submit">
        <Check className="w-4 h-4 mr-1" /> {submitting ? 'Saving...' : `Save PHQ-9 (Score: ${total} — ${severity})`}
      </Button>
    </div>
  );
}

// ============================================================
// MMSE Form
// ============================================================
const MMSE_SECTIONS = [
  { name: "Orientation to Time", max: 5, hint: "Year, Season, Date, Day, Month" },
  { name: "Orientation to Place", max: 5, hint: "State, County, City, Building, Floor" },
  { name: "Registration", max: 3, hint: "Name 3 objects, patient repeats" },
  { name: "Attention & Calculation", max: 5, hint: "Serial 7s or WORLD backwards" },
  { name: "Recall", max: 3, hint: "Recall 3 objects from registration" },
  { name: "Language & Praxis", max: 9, hint: "Naming, repetition, 3-stage command, read, write, copy" },
];

function MMSEForm({ onSubmit, submitting }) {
  const [scores, setScores] = useState({});
  const total = Object.values(scores).reduce((a, b) => a + (parseInt(b) || 0), 0);
  const severity = total >= 24 ? 'Normal' : total >= 19 ? 'Mild' : total >= 10 ? 'Moderate' : 'Severe';
  const sevColor = total >= 24 ? 'text-emerald-600' : total >= 19 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-3" data-testid="mmse-form">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Score each section:</span>
        <div className="text-right">
          <span className="text-lg font-bold font-mono">{total}</span><span className="text-xs text-muted-foreground">/30</span>
          <Badge className={`ml-2 text-[9px] ${sevColor}`}>{severity}</Badge>
        </div>
      </div>
      {MMSE_SECTIONS.map((s, i) => (
        <div key={i} className="flex items-center gap-3 p-2 rounded border bg-white" data-testid={`mmse-section-${i}`}>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium">{s.name}</span>
            <span className="text-[9px] text-muted-foreground block">{s.hint}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Input type="number" min="0" max={s.max} value={scores[s.name] || ''} placeholder="0"
              className="w-14 h-8 text-center text-sm font-mono" data-testid={`mmse-score-${i}`}
              onChange={e => setScores(prev => ({...prev, [s.name]: Math.min(parseInt(e.target.value) || 0, s.max)}))} />
            <span className="text-[10px] text-muted-foreground">/{s.max}</span>
          </div>
        </div>
      ))}
      <Button className="w-full" disabled={submitting} onClick={() => onSubmit({ task_type: 'mmse', payload: { section_scores: scores } })} data-testid="mmse-submit">
        <Check className="w-4 h-4 mr-1" /> {submitting ? 'Saving...' : `Save MMSE (Score: ${total}/30 — ${severity})`}
      </Button>
    </div>
  );
}

// ============================================================
// Medication Administration Form
// ============================================================
const ROUTES = ["PO", "IM", "IV", "SubQ", "Topical", "Inhaled", "PR", "SL"];

function MedAdminForm({ onSubmit, submitting, taskDescription }) {
  const [form, setForm] = useState({
    medication_name: taskDescription?.replace('Medication: ', '') || '',
    dose: '', route: 'IM', site: '', notes: '', billing_code: '', units: '1',
  });

  return (
    <div className="space-y-3" data-testid="med-admin-form">
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <Label className="text-xs">Medication Name</Label>
          <Input value={form.medication_name} onChange={e => setForm(f => ({...f, medication_name: e.target.value}))}
            placeholder="e.g., Rocephin, Toradol, Tylenol" className="text-sm mt-1" data-testid="med-admin-name" />
        </div>
        <div>
          <Label className="text-xs">Dose</Label>
          <Input value={form.dose} onChange={e => setForm(f => ({...f, dose: e.target.value}))}
            placeholder="e.g., 1g, 650mg" className="text-sm mt-1" data-testid="med-admin-dose" />
        </div>
        <div>
          <Label className="text-xs">Route</Label>
          <Select value={form.route} onValueChange={v => setForm(f => ({...f, route: v}))}>
            <SelectTrigger className="text-sm mt-1" data-testid="med-admin-route"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROUTES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Site (optional)</Label>
          <Input value={form.site} onChange={e => setForm(f => ({...f, site: e.target.value}))}
            placeholder="e.g., left deltoid" className="text-sm mt-1" data-testid="med-admin-site" />
        </div>
        <div>
          <Label className="text-xs">Billing Code (optional)</Label>
          <Input value={form.billing_code} onChange={e => setForm(f => ({...f, billing_code: e.target.value}))}
            placeholder="CPT/J-code" className="text-sm mt-1" data-testid="med-admin-billing" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Notes (optional)</Label>
          <Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
            placeholder="Additional notes" className="text-sm mt-1" data-testid="med-admin-notes" />
        </div>
      </div>
      <Button className="w-full" disabled={submitting || !form.medication_name || !form.dose}
        onClick={() => onSubmit({ task_type: 'med_admin', payload: form })} data-testid="med-admin-submit">
        <Pill className="w-4 h-4 mr-1" /> {submitting ? 'Recording...' : `Record: ${form.medication_name} ${form.dose} ${form.route}`}
      </Button>
    </div>
  );
}

// ============================================================
// Work / School Note Form
// ============================================================
const RETURN_OPTIONS = [
  "May return today",
  "May return tomorrow",
  "May return after 24 hours fever-free",
  "May return after 48 hours fever-free",
  "May return on specific date",
  "Restricted duties — see notes",
];

function WorkNoteForm({ onSubmit, submitting, patientName, providerName }) {
  const [form, setForm] = useState({
    note_type: 'work', return_option: 'May return today', return_date: '',
    restrictions: '', free_text: '', patient_name: patientName || 'Patient',
    provider_name: providerName || 'Provider', clinic_name: 'ChartFlow Clinic',
    visit_date: new Date().toISOString().split('T')[0],
  });
  const showDate = form.return_option === 'May return on specific date';

  return (
    <div className="space-y-3" data-testid="work-note-form">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Note Type</Label>
          <Select value={form.note_type} onValueChange={v => setForm(f => ({...f, note_type: v}))}>
            <SelectTrigger className="text-sm mt-1" data-testid="work-note-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="work">Work Note</SelectItem>
              <SelectItem value="school">School Note</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Visit Date</Label>
          <Input type="date" value={form.visit_date} onChange={e => setForm(f => ({...f, visit_date: e.target.value}))}
            className="text-sm mt-1" data-testid="work-note-visit-date" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Return Status</Label>
        <Select value={form.return_option} onValueChange={v => setForm(f => ({...f, return_option: v}))}>
          <SelectTrigger className="text-sm mt-1" data-testid="work-note-return"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RETURN_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {showDate && (
        <div>
          <Label className="text-xs">Return Date</Label>
          <Input type="date" value={form.return_date} onChange={e => setForm(f => ({...f, return_date: e.target.value}))}
            className="text-sm mt-1" data-testid="work-note-return-date" />
        </div>
      )}
      <div>
        <Label className="text-xs">Restrictions (optional)</Label>
        <Input value={form.restrictions} onChange={e => setForm(f => ({...f, restrictions: e.target.value}))}
          placeholder="e.g., No lifting >10lbs" className="text-sm mt-1" data-testid="work-note-restrictions" />
      </div>
      <div>
        <Label className="text-xs">Additional Notes (optional)</Label>
        <Textarea value={form.free_text} onChange={e => setForm(f => ({...f, free_text: e.target.value}))}
          placeholder="Any additional information..." className="text-sm mt-1 min-h-[50px]" data-testid="work-note-text" />
      </div>
      {/* Preview */}
      <div className="p-2 bg-slate-50 rounded border text-[10px] text-slate-600 font-mono leading-relaxed whitespace-pre-line" data-testid="work-note-preview">
        {form.note_type === 'work' ? 'Work' : 'School'} Note — {form.visit_date}{'\n'}
        Patient: {form.patient_name}{'\n'}
        Return: {form.return_option}{form.return_date ? ` (${form.return_date})` : ''}{'\n'}
        {form.restrictions ? `Restrictions: ${form.restrictions}\n` : ''}
        {form.free_text ? `Notes: ${form.free_text}\n` : ''}
        Provider: {form.provider_name}
      </div>
      <Button className="w-full" disabled={submitting} onClick={() => onSubmit({ task_type: 'work_note', payload: form })} data-testid="work-note-submit">
        <FileText className="w-4 h-4 mr-1" /> {submitting ? 'Generating...' : `Generate ${form.note_type === 'work' ? 'Work' : 'School'} Note`}
      </Button>
    </div>
  );
}

// ============================================================
// Flu Vaccine Form (Simplified — NOT full immunization module)
// ============================================================
function FluVaccineForm({ onSubmit, submitting }) {
  const [form, setForm] = useState({
    action: 'administered', lot_number: '', site: 'Left deltoid', decline_reason: '',
  });

  return (
    <div className="space-y-3" data-testid="flu-vaccine-form">
      <div className="flex gap-2">
        {['administered', 'declined', 'deferred'].map(a => (
          <Button key={a} variant={form.action === a ? 'default' : 'outline'} size="sm"
            className={`flex-1 text-xs ${a === 'administered' ? '' : a === 'declined' ? 'text-red-600' : 'text-amber-600'}`}
            onClick={() => setForm(f => ({...f, action: a}))} data-testid={`flu-action-${a}`}>
            {a === 'administered' ? 'Administered' : a === 'declined' ? 'Declined' : 'Deferred'}
          </Button>
        ))}
      </div>
      {form.action === 'administered' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Injection Site</Label>
            <Select value={form.site} onValueChange={v => setForm(f => ({...f, site: v}))}>
              <SelectTrigger className="text-sm mt-1" data-testid="flu-site"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Left deltoid">Left deltoid</SelectItem>
                <SelectItem value="Right deltoid">Right deltoid</SelectItem>
                <SelectItem value="Left thigh">Left thigh</SelectItem>
                <SelectItem value="Right thigh">Right thigh</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Lot # (optional)</Label>
            <Input value={form.lot_number} onChange={e => setForm(f => ({...f, lot_number: e.target.value}))}
              placeholder="Lot number" className="text-sm mt-1" data-testid="flu-lot" />
          </div>
        </div>
      )}
      {form.action === 'declined' && (
        <div>
          <Label className="text-xs">Decline Reason</Label>
          <Input value={form.decline_reason} onChange={e => setForm(f => ({...f, decline_reason: e.target.value}))}
            placeholder="e.g., patient refused, allergy concern" className="text-sm mt-1" data-testid="flu-decline-reason" />
        </div>
      )}
      <Button className={`w-full ${form.action === 'declined' ? 'bg-red-600 hover:bg-red-700' : form.action === 'deferred' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
        disabled={submitting} onClick={() => onSubmit({ task_type: 'flu_vaccine', payload: form })} data-testid="flu-submit">
        <Syringe className="w-4 h-4 mr-1" />
        {submitting ? 'Recording...' : form.action === 'administered' ? 'Record Vaccine Given' : form.action === 'declined' ? 'Record Declined' : 'Record Deferred'}
      </Button>
    </div>
  );
}

// ============================================================
// Task Executor — Shared component
// ============================================================
export default function TaskExecutor({ task, appointmentId, onComplete }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Map task descriptions to executable types
  const getTaskType = () => {
    const desc = (task.description || '').toLowerCase();
    if (desc.includes('phq') || desc.includes('depression screen')) return 'phq9';
    if (desc.includes('mmse') || desc.includes('mini-mental') || desc.includes('cognitive screen')) return 'mmse';
    if (desc.includes('work letter') || desc.includes('school note') || desc.includes('employer') || desc.includes('work note') || desc.includes('school form')) return 'work_note';
    if (desc.includes('flu vaccine') || desc.includes('influenza vaccine') || desc.includes('flu shot')) return 'flu_vaccine';
    if (task.task_type === 'INJ' || desc.includes('injection') || desc.includes('administer') || desc.includes('rocephin') || desc.includes('toradol') || desc.includes('vaccine')) return 'med_admin';
    return null;
  };

  const executableType = getTaskType();

  const handleSubmit = async (data) => {
    setSubmitting(true);
    try {
      const res = await api.post(`/appointments/${appointmentId}/tasks/${task.id}/execute`, data);
      toast.success(res.data.result?.summary || 'Task completed');
      setOpen(false);
      onComplete?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to execute task');
    } finally { setSubmitting(false); }
  };

  if (!executableType || task.status !== 'pending') return null;

  return (
    <>
      {!open ? (
        <Button variant="default" size="sm" className="h-7 text-xs gap-1"
          onClick={() => setOpen(true)} data-testid={`open-task-form-${task.id}`}>
          {executableType === 'phq9' || executableType === 'mmse' ? <Brain className="w-3 h-3" /> :
           executableType === 'work_note' ? <FileText className="w-3 h-3" /> :
           executableType === 'flu_vaccine' ? <Syringe className="w-3 h-3" /> :
           <Pill className="w-3 h-3" />}
          {executableType === 'phq9' ? 'Start PHQ-9' :
           executableType === 'mmse' ? 'Start MMSE' :
           executableType === 'work_note' ? 'Write Note' :
           executableType === 'flu_vaccine' ? 'Record Vaccine' :
           'Record Med'}
        </Button>
      ) : (
        <Card className="border-2 border-primary/20 mt-2" data-testid={`task-form-${executableType}`}>
          <CardHeader className="py-2 px-3 flex-row items-center justify-between">
            <CardTitle className="text-sm">
              {executableType === 'phq9' ? 'PHQ-9 Depression Screening' :
               executableType === 'mmse' ? 'MMSE Cognitive Screening' :
               executableType === 'work_note' ? 'Work / School Note' :
               executableType === 'flu_vaccine' ? 'Influenza Vaccine' :
               'Medication Administration'}
            </CardTitle>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setOpen(false)}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            {executableType === 'phq9' && <PHQ9Form onSubmit={handleSubmit} submitting={submitting} />}
            {executableType === 'mmse' && <MMSEForm onSubmit={handleSubmit} submitting={submitting} />}
            {executableType === 'med_admin' && <MedAdminForm onSubmit={handleSubmit} submitting={submitting} taskDescription={task.description} />}
            {executableType === 'work_note' && <WorkNoteForm onSubmit={handleSubmit} submitting={submitting} />}
            {executableType === 'flu_vaccine' && <FluVaccineForm onSubmit={handleSubmit} submitting={submitting} />}
          </CardContent>
        </Card>
      )}
    </>
  );
}
